<template>
  <popup-dialog
    :visible="visible"
    class="popup-movepopup"
    :close-button-visible="true"
    :title="$t('labels.moveModel')"
    @close="close"
  >
    <form class="popup-movepopup__form" @submit.prevent="move()">
      <input-workspace-folder
        v-model:workspaceId="data.workspaceId"
        v-model:folderId="data.folderId"
        :errors="{
          workspace: data.fieldErrors?.workspace_id,
          folder: data.fieldErrors?.folder_id
        }"
        @update:folder-slug="data.folderSlug = $event"
        @update:folder-id="data.fieldErrors.folder_id = undefined"
        @update:workspace-id="data.fieldErrors.workspace_id = undefined"
      />

      <button class="button popup-movepopup__button mt-4" type="submit">
        {{ $t("labels.move") }}
      </button>
    </form>
  </popup-dialog>
</template>

<script setup lang="ts">
import { reactive, watch } from "vue";
import PopupDialog from "@core/ui/PopupDialog.vue";
import InputWorkspaceFolder from "@core/input/InputWorkspaceFolder.vue";
import { useModels } from "@/api/models.store";
import { showError } from "@core/ui/alert/alert";

import type { Model } from "@/api/Model";
import { BadRequestError } from "@core/errors/Errors";

const props = withDefaults(defineProps<{
  visible: boolean;
  model: Model;
}>(), {
  visible: false,
});

const emit = defineEmits<{
  (e: "close"): void;
  (e: "moved", model: Model): void;
}>();

const { moveModel } = useModels();
    
const data = reactive({
  workspaceId: undefined as undefined | number,
  folderId: undefined as undefined | number,
  folderSlug: undefined as undefined | string,
  fieldErrors: {} as {
    workspace_id?: string[];
    folder_id?: string[];
  }
});

watch(() => props.model.id, () => {
  data.workspaceId = props.model?.workspace;
  data.folderId = props.model?.folder;
  data.folderSlug = props.model?.folderSlug; // only
}, { immediate: true });


async function move() {
  try {
    if (data.folderId == null) return;
    if (data.folderSlug == null) return;

    const newModel = await moveModel({
      id: props.model.id,
      workspaceId: data.workspaceId ?? props.model.workspace,
      folderId: data.folderId,
      folderSlug: data.folderSlug
    });
    close();
    emit("moved", newModel);
  } catch (_error) {
    const error = _error as BadRequestError;
    data.fieldErrors = error.fields || {};
    showError(error);
  }
}

function close() {
  emit("close");
}
</script>

<style lang="scss">
.popup-movepopup {
  overflow: visible;

  &__form {
    padding: 2rem 2rem;
    display: flex;
    gap: 0.5rem;
    flex-direction: column;
  }

  &__button {
    align-self: center;
  }
}
</style>
